import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calculator, TrendingUp, DollarSign, BarChart3, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency, formatHumanReadable } from '@/utils/currency';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

export default function FinancialCalculators({ user, onLogout }) {
  // SIP Calculator State
  const [sipData, setSipData] = useState({
    monthlyInvestment: '',
    expectedReturn: 12,
    timePeriod: 10
  });
  const [sipResult, setSipResult] = useState(null);

  // Step-up SIP State
  const [stepUpSipData, setStepUpSipData] = useState({
    monthlyInvestment: '',
    expectedReturn: 12,
    timePeriod: 10,
    annualIncrease: 10
  });
  const [stepUpSipResult, setStepUpSipResult] = useState(null);

  // Lumpsum Calculator State
  const [lumpsumData, setLumpsumData] = useState({
    investment: '',
    expectedReturn: 12,
    timePeriod: 10
  });
  const [lumpsumResult, setLumpsumResult] = useState(null);

  // CAGR Calculator State
  const [cagrData, setCagrData] = useState({
    initialValue: '',
    finalValue: '',
    years: ''
  });
  const [cagrResult, setCagrResult] = useState(null);
  const isMobile = useIsMobile();

  // Calculate Normal SIP
  const calculateSIP = () => {
    const P = parseFloat(sipData.monthlyInvestment);
    const r = parseFloat(sipData.expectedReturn) / 100 / 12;
    const n = parseFloat(sipData.timePeriod) * 12;

    if (!P || !n) return;

    const M = P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    const invested = P * n;
    const returns = M - invested;

    // Generate year-wise data
    const chartData = [];
    for (let year = 1; year <= sipData.timePeriod; year++) {
      const months = year * 12;
      const value = P * ((Math.pow(1 + r, months) - 1) / r) * (1 + r);
      const investedTillNow = P * months;
      chartData.push({
        year: `Year ${year}`,
        invested: investedTillNow,
        value: value
      });
    }

    setSipResult({
      maturityAmount: M,
      totalInvested: invested,
      totalReturns: returns,
      chartData
    });
  };

  // Calculate Step-up SIP
  const calculateStepUpSIP = () => {
    let P = parseFloat(stepUpSipData.monthlyInvestment);
    const r = parseFloat(stepUpSipData.expectedReturn) / 100 / 12;
    const years = parseFloat(stepUpSipData.timePeriod);
    const increase = parseFloat(stepUpSipData.annualIncrease) / 100;

    if (!P || !years) return;

    let totalValue = 0;
    let totalInvested = 0;
    const chartData = [];

    for (let year = 1; year <= years; year++) {
      for (let month = 1; month <= 12; month++) {
        const remainingMonths = (years - year) * 12 + (12 - month);
        const futureValue = P * Math.pow(1 + r, remainingMonths);
        totalValue += futureValue;
        totalInvested += P;
      }
      
      chartData.push({
        year: `Year ${year}`,
        invested: totalInvested,
        value: totalValue
      });
      
      P = P * (1 + increase);
    }

    const returns = totalValue - totalInvested;

    setStepUpSipResult({
      maturityAmount: totalValue,
      totalInvested: totalInvested,
      totalReturns: returns,
      chartData
    });
  };

  // Calculate Lumpsum
  const calculateLumpsum = () => {
    const P = parseFloat(lumpsumData.investment);
    const r = parseFloat(lumpsumData.expectedReturn) / 100;
    const t = parseFloat(lumpsumData.timePeriod);

    if (!P || !t) return;

    const A = P * Math.pow(1 + r, t);
    const returns = A - P;

    // Generate year-wise data
    const chartData = [];
    for (let year = 1; year <= t; year++) {
      const value = P * Math.pow(1 + r, year);
      chartData.push({
        year: `Year ${year}`,
        invested: P,
        value: value
      });
    }

    setLumpsumResult({
      maturityAmount: A,
      totalInvested: P,
      totalReturns: returns,
      chartData
    });
  };

  // Calculate CAGR
  const calculateCAGR = () => {
    const initial = parseFloat(cagrData.initialValue);
    const final = parseFloat(cagrData.finalValue);
    const years = parseFloat(cagrData.years);

    if (!initial || !final || !years) return;

    const cagr = (Math.pow(final / initial, 1 / years) - 1) * 100;
    const totalReturns = final - initial;
    const totalReturnsPercent = ((final - initial) / initial) * 100;

    setCagrResult({
      cagr: cagr,
      totalReturns: totalReturns,
      totalReturnsPercent: totalReturnsPercent,
      initialValue: initial,
      finalValue: final
    });
  };

  return (
    <div className="min-h-screen p-4 lg:p-8" data-testid="calculators-page">
      <div className="max-w-7xl mx-auto relative">
        <Navigation user={user} onLogout={onLogout} currentPage="/calculators" />
        {!isMobile && (
          <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
            <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-blue-600/[0.06] to-indigo-500/[0.06] blur-3xl" animate={{ x: [0, 25, 0], y: [0, -20, 0] }} transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }} style={{ top: '5%', right: '10%' }} />
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-blue-500 to-indigo-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100">
              <span className="bg-gradient-to-r from-blue-500 to-indigo-400 bg-clip-text text-transparent">Financial</span> Calculators
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm">Plan your investments with powerful calculation tools</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
        <Tabs defaultValue="sip" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8 bg-white dark:bg-gray-800 p-2 rounded-xl shadow-sm">
            <TabsTrigger value="sip" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white dark:text-gray-300">
              <TrendingUp className="w-4 h-4 mr-2" />
              SIP
            </TabsTrigger>
            <TabsTrigger value="stepup" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-600 data-[state=active]:text-white dark:text-gray-300">
              <Zap className="w-4 h-4 mr-2" />
              Step-up SIP
            </TabsTrigger>
            <TabsTrigger value="lumpsum" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white dark:text-gray-300">
              <DollarSign className="w-4 h-4 mr-2" />
              Lumpsum
            </TabsTrigger>
            <TabsTrigger value="cagr" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white dark:text-gray-300">
              <BarChart3 className="w-4 h-4 mr-2" />
              CAGR
            </TabsTrigger>
          </TabsList>

          {/* Normal SIP Calculator */}
          <TabsContent value="sip">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="glass p-6 dark:bg-gray-800">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">SIP Calculator</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="dark:text-gray-300">Monthly Investment Amount</Label>
                    <Input
                      type="number"
                      value={sipData.monthlyInvestment}
                      onChange={(e) => setSipData({ ...sipData, monthlyInvestment: e.target.value })}
                      placeholder="10000"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Expected Return Rate (% p.a.)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={sipData.expectedReturn}
                      onChange={(e) => setSipData({ ...sipData, expectedReturn: e.target.value })}
                      placeholder="12"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Time Period (Years)</Label>
                    <Input
                      type="number"
                      value={sipData.timePeriod}
                      onChange={(e) => setSipData({ ...sipData, timePeriod: e.target.value })}
                      placeholder="10"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <Button
                    onClick={calculateSIP}
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                  >
                    Calculate SIP
                  </Button>
                </div>
              </Card>

              {sipResult && (
                <Card className="glass p-6 dark:bg-gray-800">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Results</h3>
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Maturity Amount</p>
                      <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(sipResult.maturityAmount, 'INR')}
                      </p>
                      <p className="text-sm text-emerald-500 dark:text-emerald-300 mt-1">
                        ({formatHumanReadable(sipResult.maturityAmount, 'INR')})
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Invested</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {formatCurrency(sipResult.totalInvested, 'INR')}
                        </p>
                        <p className="text-xs text-blue-500 dark:text-blue-300 mt-1">
                          ({formatHumanReadable(sipResult.totalInvested, 'INR')})
                        </p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Returns</p>
                        <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {formatCurrency(sipResult.totalReturns, 'INR')}
                        </p>
                        <p className="text-xs text-purple-500 dark:text-purple-300 mt-1">
                          ({formatHumanReadable(sipResult.totalReturns, 'INR')})
                        </p>
                      </div>
                    </div>
                    <div className="mt-6">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Growth Over Time</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={sipResult.chartData}>
                          <XAxis dataKey="year" />
                          <YAxis tickFormatter={(value) => formatHumanReadable(value, 'INR')} />
                          <Tooltip formatter={(value) => [formatCurrency(value, 'INR'), '']} />
                          <Legend />
                          <Line type="monotone" dataKey="invested" stroke="#3b82f6" name="Invested" />
                          <Line type="monotone" dataKey="value" stroke="#10b981" name="Value" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Step-up SIP Calculator */}
          <TabsContent value="stepup">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="glass p-6 dark:bg-gray-800">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Step-up SIP Calculator</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="dark:text-gray-300">Initial Monthly Investment</Label>
                    <Input
                      type="number"
                      value={stepUpSipData.monthlyInvestment}
                      onChange={(e) => setStepUpSipData({ ...stepUpSipData, monthlyInvestment: e.target.value })}
                      placeholder="10000"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Expected Return Rate (% p.a.)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={stepUpSipData.expectedReturn}
                      onChange={(e) => setStepUpSipData({ ...stepUpSipData, expectedReturn: e.target.value })}
                      placeholder="12"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Time Period (Years)</Label>
                    <Input
                      type="number"
                      value={stepUpSipData.timePeriod}
                      onChange={(e) => setStepUpSipData({ ...stepUpSipData, timePeriod: e.target.value })}
                      placeholder="10"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Annual Increase (% per year)</Label>
                    <Input
                      type="number"
                      step="1"
                      value={stepUpSipData.annualIncrease}
                      onChange={(e) => setStepUpSipData({ ...stepUpSipData, annualIncrease: e.target.value })}
                      placeholder="10"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <Button
                    onClick={calculateStepUpSIP}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
                  >
                    Calculate Step-up SIP
                  </Button>
                </div>
              </Card>

              {stepUpSipResult && (
                <Card className="glass p-6 dark:bg-gray-800">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Results</h3>
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Maturity Amount</p>
                      <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(stepUpSipResult.maturityAmount, 'INR')}
                      </p>
                      <p className="text-sm text-emerald-500 dark:text-emerald-300 mt-1">
                        ({formatHumanReadable(stepUpSipResult.maturityAmount, 'INR')})
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Invested</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {formatCurrency(stepUpSipResult.totalInvested, 'INR')}
                        </p>
                        <p className="text-xs text-blue-500 dark:text-blue-300 mt-1">
                          ({formatHumanReadable(stepUpSipResult.totalInvested, 'INR')})
                        </p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Returns</p>
                        <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {formatCurrency(stepUpSipResult.totalReturns, 'INR')}
                        </p>
                        <p className="text-xs text-purple-500 dark:text-purple-300 mt-1">
                          ({formatHumanReadable(stepUpSipResult.totalReturns, 'INR')})
                        </p>
                      </div>
                    </div>
                    <div className="mt-6">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Growth Over Time</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={stepUpSipResult.chartData}>
                          <XAxis dataKey="year" />
                          <YAxis tickFormatter={(value) => formatHumanReadable(value, 'INR')} />
                          <Tooltip formatter={(value) => [formatCurrency(value, 'INR'), '']} />
                          <Legend />
                          <Line type="monotone" dataKey="invested" stroke="#8b5cf6" name="Invested" />
                          <Line type="monotone" dataKey="value" stroke="#10b981" name="Value" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Lumpsum Calculator */}
          <TabsContent value="lumpsum">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="glass p-6 dark:bg-gray-800">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Lumpsum Calculator</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="dark:text-gray-300">Investment Amount</Label>
                    <Input
                      type="number"
                      value={lumpsumData.investment}
                      onChange={(e) => setLumpsumData({ ...lumpsumData, investment: e.target.value })}
                      placeholder="100000"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Expected Return Rate (% p.a.)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={lumpsumData.expectedReturn}
                      onChange={(e) => setLumpsumData({ ...lumpsumData, expectedReturn: e.target.value })}
                      placeholder="12"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Time Period (Years)</Label>
                    <Input
                      type="number"
                      value={lumpsumData.timePeriod}
                      onChange={(e) => setLumpsumData({ ...lumpsumData, timePeriod: e.target.value })}
                      placeholder="10"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <Button
                    onClick={calculateLumpsum}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                  >
                    Calculate Lumpsum
                  </Button>
                </div>
              </Card>

              {lumpsumResult && (
                <Card className="glass p-6 dark:bg-gray-800">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Results</h3>
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Maturity Amount</p>
                      <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(lumpsumResult.maturityAmount, 'INR')}
                      </p>
                      <p className="text-sm text-emerald-500 dark:text-emerald-300 mt-1">
                        ({formatHumanReadable(lumpsumResult.maturityAmount, 'INR')})
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Invested</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {formatCurrency(lumpsumResult.totalInvested, 'INR')}
                        </p>
                        <p className="text-xs text-blue-500 dark:text-blue-300 mt-1">
                          ({formatHumanReadable(lumpsumResult.totalInvested, 'INR')})
                        </p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Returns</p>
                        <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {formatCurrency(lumpsumResult.totalReturns, 'INR')}
                        </p>
                        <p className="text-xs text-purple-500 dark:text-purple-300 mt-1">
                          ({formatHumanReadable(lumpsumResult.totalReturns, 'INR')})
                        </p>
                      </div>
                    </div>
                    <div className="mt-6">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Growth Over Time</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={lumpsumResult.chartData}>
                          <XAxis dataKey="year" />
                          <YAxis tickFormatter={(value) => formatHumanReadable(value, 'INR')} />
                          <Tooltip formatter={(value) => [formatCurrency(value, 'INR'), '']} />
                          <Legend />
                          <Line type="monotone" dataKey="invested" stroke="#3b82f6" name="Invested" />
                          <Line type="monotone" dataKey="value" stroke="#10b981" name="Value" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* CAGR Calculator */}
          <TabsContent value="cagr">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="glass p-6 dark:bg-gray-800">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">CAGR Calculator</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="dark:text-gray-300">Initial Investment Value</Label>
                    <Input
                      type="number"
                      value={cagrData.initialValue}
                      onChange={(e) => setCagrData({ ...cagrData, initialValue: e.target.value })}
                      placeholder="100000"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Final Investment Value</Label>
                    <Input
                      type="number"
                      value={cagrData.finalValue}
                      onChange={(e) => setCagrData({ ...cagrData, finalValue: e.target.value })}
                      placeholder="250000"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="dark:text-gray-300">Investment Duration (Years)</Label>
                    <Input
                      type="number"
                      value={cagrData.years}
                      onChange={(e) => setCagrData({ ...cagrData, years: e.target.value })}
                      placeholder="5"
                      className="dark:bg-gray-700 dark:text-gray-100"
                    />
                  </div>
                  <Button
                    onClick={calculateCAGR}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  >
                    Calculate CAGR
                  </Button>
                </div>
              </Card>

              {cagrResult && (
                <Card className="glass p-6 dark:bg-gray-800">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Results</h3>
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">CAGR (Compound Annual Growth Rate)</p>
                      <p className="text-4xl font-bold text-amber-600 dark:text-amber-400">
                        {cagrResult.cagr.toFixed(2)}%
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Per Annum</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Initial Value</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {formatCurrency(cagrResult.initialValue, 'INR')}
                        </p>
                        <p className="text-xs text-blue-500 dark:text-blue-300 mt-1">
                          ({formatHumanReadable(cagrResult.initialValue, 'INR')})
                        </p>
                      </div>
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Final Value</p>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(cagrResult.finalValue, 'INR')}
                        </p>
                        <p className="text-xs text-emerald-500 dark:text-emerald-300 mt-1">
                          ({formatHumanReadable(cagrResult.finalValue, 'INR')})
                        </p>
                      </div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Returns</p>
                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {formatCurrency(cagrResult.totalReturns, 'INR')}
                      </p>
                      <p className="text-xs text-purple-500 dark:text-purple-300">
                        ({formatHumanReadable(cagrResult.totalReturns, 'INR')})
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        ({cagrResult.totalReturnsPercent.toFixed(2)}% growth)
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-xl">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">What is CAGR?</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        CAGR is the mean annual growth rate of an investment over a specified period longer than one year. 
                        It represents one of the most accurate ways to calculate returns for individual assets.
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
        </motion.div>
        <Footer />
      </div>
    </div>
  );
}
